/**
 * Страница настроек
 * Современный тёмный дизайн с красивыми вкладками
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import MagneticButton from '../components/MagneticButton';
import AnimatedCard from '../components/AnimatedCard';
import GlowingEffect from '../components/GlowingEffect';
import { showToast, initToast } from '../components/Toast';
import Telegram from './Telegram';
import { API_URL } from '../utils/api.js';
import { isNative } from '../utils/native.js';

export default function Settings() {
  const urlParams = new URLSearchParams(window.location.search);
  const savedTab = localStorage.getItem('settings_active_tab');
  const initialTab = urlParams.get('tab') || savedTab || 'api';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('settings_active_tab', tabId);
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('tab', tabId);
    window.history.pushState({}, '', newUrl);
  };

  const [settings, setSettings] = useState({
    telegram_bot_token: '',
    telegram_api_id: '',
    telegram_api_hash: '',
    domain: '',
    certbot_email: '',
  });

  // Получаем текущего пользователя (с кешированием для производительности)
  let cachedUserId = null;
  let userIdCacheTime = 0;
  const USER_ID_CACHE_TTL = 60000; // 1 минута
  
  const getCurrentUserId = async (forceRefresh = false) => {
    try {
      // Проверяем кеш
      if (!forceRefresh && cachedUserId && Date.now() - userIdCacheTime < USER_ID_CACHE_TTL) {
        return cachedUserId;
      }
      
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.id) {
          cachedUserId = user.id;
          userIdCacheTime = Date.now();
          return user.id;
        }
      }
      // Пробуем получить через API
      const res = await axios.get(`${API_URL}/user/me`);
      if (res.data?.user?.id) {
        cachedUserId = res.data.user.id;
        userIdCacheTime = Date.now();
        return res.data.user.id;
      }
    } catch (e) {
      console.warn('[Settings] Ошибка получения user_id:', e);
    }
    return null;
  };

  // Функция-обертка для безопасной работы с sessionStorage
  // Всегда проверяет user_id перед сохранением/загрузкой
  const safeSessionStorage = {
    // Получить данные из sessionStorage с проверкой user_id
    get: async () => {
      try {
        const saved = sessionStorage.getItem('youtube_settings_draft');
        if (!saved) {
          return null;
        }
        
        const parsed = JSON.parse(saved);
        const currentUserId = await getCurrentUserId();
        
        // Если нет user_id в сохраненных данных - очищаем (старый формат)
        if (!parsed.user_id) {
          console.log('[Settings] Обнаружены черновики без user_id (старый формат), очищаем sessionStorage');
          sessionStorage.removeItem('youtube_settings_draft');
          return null;
        }
        
        // Если user_id не совпадает - очищаем
        if (currentUserId && parsed.user_id !== currentUserId) {
          console.log('[Settings] Черновики принадлежат другому пользователю, очищаем sessionStorage', {
            savedUserId: parsed.user_id,
            currentUserId: currentUserId
          });
          sessionStorage.removeItem('youtube_settings_draft');
          return null;
        }
        
        // Если пользователь не авторизован - очищаем
        if (!currentUserId) {
          console.log('[Settings] Пользователь не авторизован, очищаем черновики');
          sessionStorage.removeItem('youtube_settings_draft');
          return null;
        }
        
        // Данные принадлежат текущему пользователю
        const { user_id, ...settings } = parsed;
        return settings;
      } catch (e) {
        console.warn('[Settings] Ошибка чтения из sessionStorage:', e);
        return null;
      }
    },
    
    // Сохранить данные в sessionStorage с проверкой user_id
    set: async (settings) => {
      try {
        const currentUserId = await getCurrentUserId();
        
        if (!currentUserId) {
          console.warn('[Settings] Не удалось получить user_id, черновики не сохранены');
          return false;
        }
        
        const settingsWithUserId = {
          ...settings,
          user_id: currentUserId
        };
        
        sessionStorage.setItem('youtube_settings_draft', JSON.stringify(settingsWithUserId));
        console.log('[Settings] Черновики сохранены в sessionStorage для пользователя:', currentUserId);
        return true;
      } catch (e) {
        console.warn('[Settings] Ошибка сохранения в sessionStorage:', e);
        return false;
      }
    },
    
    // Очистить sessionStorage
    clear: () => {
      try {
        sessionStorage.removeItem('youtube_settings_draft');
        console.log('[Settings] Черновики очищены из sessionStorage');
      } catch (e) {
        console.warn('[Settings] Ошибка очистки sessionStorage:', e);
      }
    }
  };

  // Восстанавливаем введенные данные из sessionStorage при загрузке
  // С проверкой, что данные принадлежат текущему пользователю
  const getInitialYoutubeSettings = async () => {
    const settings = await safeSessionStorage.get();
    return settings;
  };

  const [youtubeSettings, setYoutubeSettings] = useState({
    client_id: '',
    client_secret: '',
    refresh_token: '',
    channel_id: '',
    redirect_uri: '',
  });
  
  // Флаг для отслеживания, были ли загружены настройки с сервера
  const [youtubeSettingsLoaded, setYoutubeSettingsLoaded] = useState(false);
  
  // Сохраняем черновики в sessionStorage при каждом изменении с привязкой к user_id
  // Обновляет state синхронно для отзывчивости UI, сохранение в sessionStorage - асинхронно
  const updateYoutubeSettings = (updater) => {
    setYoutubeSettings(prev => {
      const newSettings = typeof updater === 'function' ? updater(prev) : updater;
      
      // Сохраняем в sessionStorage асинхронно с проверкой user_id (не блокируем UI)
      safeSessionStorage.set(newSettings).catch(err => {
        console.warn('[Settings] Ошибка сохранения черновиков:', err);
      });
      
      return newSettings;
    });
  };

  const [authSuccess, setAuthSuccess] = useState(false);

  // Функция для применения токена из любого источника
  const applyRefreshToken = (token) => {
    if (token) {
      console.log('[Settings] Применяем refresh token');
      updateYoutubeSettings(prev => ({
        ...prev,
        refresh_token: token
      }));
      setAuthSuccess(true);
      showToast('✅ Refresh Token успешно получен и добавлен в настройки! Нажмите "Сохранить настройки YouTube" для завершения.', 'success', 5000);
      setTimeout(() => setAuthSuccess(false), 5000);
    }
  };

  useEffect(() => {
    initToast();
    fetchSettings();
    
    // При первой загрузке проверяем черновики из sessionStorage
    // Если они есть и принадлежат текущему пользователю, используем их
    const loadInitialSettings = async () => {
      try {
        // Используем safeSessionStorage.get() который автоматически проверяет user_id
        const draft = await safeSessionStorage.get();
        
        if (draft) {
          const hasDraft = draft.client_id || draft.client_secret || draft.refresh_token || draft.channel_id;
          if (hasDraft) {
            console.log('[Settings] Найдены черновики в sessionStorage для текущего пользователя, используем их вместо загрузки с сервера');
            setYoutubeSettings(draft);
            // Убеждаемся, что черновики сохранены с правильным user_id
            safeSessionStorage.set(draft).catch(err => {
              console.warn('[Settings] Ошибка сохранения черновиков при загрузке:', err);
            });
            setYoutubeSettingsLoaded(true);
            return; // НЕ вызываем fetchYouTubeSettings() - черновики имеют приоритет
          }
        }
      } catch (e) {
        console.warn('[Settings] Ошибка проверки черновиков:', e);
      }
      
      // Черновиков нет или они принадлежат другому пользователю - загружаем с сервера
      fetchYouTubeSettings();
    };
    
    loadInitialSettings();
    
    // Обработчик postMessage
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'youtube-auth-success') {
        console.log('[Settings] Получен refresh token через postMessage');
        applyRefreshToken(event.data.refreshToken);
        // Очищаем localStorage после получения
        try {
          localStorage.removeItem('youtube_refresh_token_new');
          localStorage.removeItem('youtube_auth_success');
        } catch (e) {}
        // Обновляем настройки, но сохраняем введенные пользователем данные
        setTimeout(() => fetchYouTubeSettings(true), 500);
      }
    };
    
    // Функция проверки localStorage
    const checkForNewToken = () => {
      try {
        const authTime = localStorage.getItem('youtube_auth_success');
        const token = localStorage.getItem('youtube_refresh_token_new');
        
        if (authTime && token) {
          const timeDiff = Date.now() - parseInt(authTime);
          // Если токен был получен менее 5 минут назад
          if (timeDiff < 300000) {
            console.log('[Settings] Найден новый refresh token в localStorage');
            applyRefreshToken(token);
            // Очищаем после использования
            localStorage.removeItem('youtube_refresh_token_new');
            localStorage.removeItem('youtube_auth_success');
            return true;
          } else {
            // Старый токен, очищаем
            localStorage.removeItem('youtube_refresh_token_new');
            localStorage.removeItem('youtube_auth_success');
          }
        }
      } catch (e) {}
      return false;
    };
    
    // Проверка localStorage каждую секунду
    const checkLocalStorage = setInterval(checkForNewToken, 1000);
    
    // Также проверяем при фокусе на окно (когда пользователь возвращается из OAuth)
    // НЕ обновляем настройки автоматически, чтобы не перезаписывать введенные пользователем данные
    const handleFocus = () => {
      console.log('[Settings] Окно получило фокус, проверяем токен...');
      // Только проверяем токен, НЕ обновляем все настройки
      checkForNewToken();
      // НЕ вызываем fetchYouTubeSettings() - это перезапишет введенные данные
    };
    
    // Также проверяем при visibility change
    // НЕ обновляем настройки автоматически, чтобы не перезаписывать введенные пользователем данные
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Settings] Страница стала видимой, проверяем токен...');
        // Только проверяем токен, НЕ обновляем все настройки
        checkForNewToken();
        // НЕ вызываем fetchYouTubeSettings() - это перезапишет введенные данные
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromUrl = urlParams.get('tab');
      if (tabFromUrl && tabFromUrl !== activeTab) {
        setActiveTab(tabFromUrl);
        localStorage.setItem('settings_active_tab', tabFromUrl);
      }
    };
    
    window.addEventListener('message', handleMessage);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(checkLocalStorage);
    };
  }, []);
  
  // Отслеживаем изменения пользователя и очищаем sessionStorage при смене
  useEffect(() => {
    const checkUserChange = async () => {
      try {
        const saved = sessionStorage.getItem('youtube_settings_draft');
        if (saved) {
          const parsed = JSON.parse(saved);
          const currentUserId = await getCurrentUserId();
          
          // Если нет user_id в сохраненных данных - очищаем (старый формат)
          if (!parsed.user_id) {
            console.log('[Settings] Обнаружены черновики без user_id (старый формат), очищаем sessionStorage');
            safeSessionStorage.clear();
            // Загружаем настройки с сервера, если компонент уже загружен
            if (youtubeSettingsLoaded) {
              // Используем setTimeout чтобы избежать проблем с зависимостями
              setTimeout(() => {
                fetchYouTubeSettings().catch(err => {
                  console.warn('[Settings] Ошибка загрузки настроек после очистки:', err);
                });
              }, 100);
            }
            return;
          }
          
          // Если user_id не совпадает - очищаем
          if (currentUserId && parsed.user_id !== currentUserId) {
            console.log('[Settings] Обнаружена смена пользователя, очищаем черновики YouTube настроек', {
              savedUserId: parsed.user_id,
              currentUserId: currentUserId
            });
            safeSessionStorage.clear();
            // Загружаем настройки нового пользователя с сервера, если компонент уже загружен
            if (youtubeSettingsLoaded) {
              setTimeout(() => {
                fetchYouTubeSettings().catch(err => {
                  console.warn('[Settings] Ошибка загрузки настроек после смены пользователя:', err);
                });
              }, 100);
            }
          }
          
          // Если пользователь не авторизован - очищаем
          if (!currentUserId) {
            console.log('[Settings] Пользователь не авторизован, очищаем черновики');
            safeSessionStorage.clear();
          }
        }
      } catch (e) {
        console.warn('[Settings] Ошибка проверки смены пользователя:', e);
      }
    };
    
    // Проверяем при монтировании компонента
    checkUserChange();
    
    // Отслеживаем изменения localStorage.user
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        console.log('[Settings] Обнаружено изменение localStorage.user, проверяем черновики');
        checkUserChange();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Также проверяем периодически (на случай, если событие storage не сработало)
    const userCheckInterval = setInterval(checkUserChange, 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(userCheckInterval);
    };
  }, [youtubeSettingsLoaded]); // Зависимость от youtubeSettingsLoaded

  const fetchYouTubeSettings = async (preserveUserInput = false) => {
    try {
      const res = await axios.get(`${API_URL}/youtube/settings`);
      if (res.data.settings) {
        const currentDomain = settings.domain || window.location.hostname;
        const defaultRedirectUri = `https://${currentDomain}/api/youtube/auth/callback`;
        
        // Если нужно сохранить введенные пользователем данные, объединяем их с данными с сервера
        if (preserveUserInput && youtubeSettingsLoaded) {
          const mergedSettings = {
            client_id: res.data.settings.client_id?.value || youtubeSettings.client_id || '',
            client_secret: res.data.settings.client_secret?.value || youtubeSettings.client_secret || '',
            refresh_token: res.data.settings.refresh_token?.value || youtubeSettings.refresh_token || '',
            channel_id: res.data.settings.channel_id?.value || youtubeSettings.channel_id || '',
            redirect_uri: res.data.settings.redirect_uri?.value || youtubeSettings.redirect_uri || defaultRedirectUri,
          };
          setYoutubeSettings(mergedSettings);
          await safeSessionStorage.set(mergedSettings);
        } else {
          // Проверяем черновики из sessionStorage с проверкой user_id
          // Используем safeSessionStorage.get() который автоматически проверяет user_id
          const draft = await safeSessionStorage.get();
          const hasDraft = draft && (draft.client_id || draft.client_secret || draft.refresh_token || draft.channel_id);
          
          if (hasDraft) {
            // Черновики имеют приоритет - используем их, но дополняем пустые поля данными с сервера
            console.log('[Settings] Обнаружены черновики для текущего пользователя, используем их с приоритетом');
            const mergedSettings = {
              client_id: draft.client_id || res.data.settings.client_id?.value || '',
              client_secret: draft.client_secret || res.data.settings.client_secret?.value || '',
              refresh_token: draft.refresh_token || res.data.settings.refresh_token?.value || '',
              channel_id: draft.channel_id || res.data.settings.channel_id?.value || '',
              redirect_uri: draft.redirect_uri || res.data.settings.redirect_uri?.value || defaultRedirectUri,
            };
            setYoutubeSettings(mergedSettings);
            // Сохраняем объединенные настройки с правильным user_id
            await safeSessionStorage.set(mergedSettings);
          } else {
            // Черновиков нет - загружаем с сервера
            const serverSettings = {
              client_id: res.data.settings.client_id?.value || '',
              client_secret: res.data.settings.client_secret?.value || '',
              refresh_token: res.data.settings.refresh_token?.value || '',
              channel_id: res.data.settings.channel_id?.value || '',
              redirect_uri: res.data.settings.redirect_uri?.value || defaultRedirectUri,
            };
            setYoutubeSettings(serverSettings);
            // Сохраняем настройки с сервера с правильным user_id
            await safeSessionStorage.set(serverSettings);
          }
          setYoutubeSettingsLoaded(true);
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек YouTube:', error);
    }
  };

  const handleSaveYouTube = async () => {
    try {
      const currentDomain = settings.domain || window.location.hostname;
      const defaultRedirectUri = `https://${currentDomain}/api/youtube/auth/callback`;
      
      const settingsToSave = {
        ...youtubeSettings,
        redirect_uri: youtubeSettings.redirect_uri || defaultRedirectUri,
      };
      
      const res = await axios.post(`${API_URL}/youtube/settings`, { settings: settingsToSave });
      if (res.data.success) {
        showToast('✅ Настройки YouTube успешно сохранены!', 'success');
        // Очищаем черновики после успешного сохранения
        safeSessionStorage.clear();
        await fetchYouTubeSettings();
      }
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      showToast('❌ Ошибка при сохранении: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`);
      if (res.data.success && res.data.settings) {
        setSettings({
          telegram_bot_token: res.data.settings.telegram_bot_token || '',
          telegram_api_id: res.data.settings.telegram_api_id || '',
          telegram_api_hash: res.data.settings.telegram_api_hash || '',
          domain: res.data.settings.domain || window.location.hostname,
          certbot_email: res.data.settings.certbot_email || '',
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
    }
  };

  const tabs = [
    { id: 'api', label: 'API Ключи', icon: '🔑' },
    { id: 'youtube', label: 'YouTube', icon: '📹' },
    { id: 'telegram', label: 'Телеграм', icon: '💬' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-tighter">
          ⚙️ Настройки
        </h1>
        <p className="text-slate-400 tracking-tight">Конфигурация интеграций и API</p>
      </motion.div>

      {/* Вкладки */}
      <div className="relative p-2 rounded-[1.5rem] border-[0.75px] border-white/5 mb-6">
        <GlowingEffect
          spread={40}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
          borderWidth={2}
          variant="default"
        />
        <div className="relative p-2 overflow-hidden bg-white/[0.02] backdrop-blur-sm rounded-xl border border-white/5">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  relative flex-1 min-w-[120px] px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 tracking-tight
                  ${activeTab === tab.id
                    ? 'bg-white/10 text-white border border-white/10'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                <span className="relative z-10">{tab.icon}</span>
                <span className="relative z-10 hidden sm:inline">{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Контент вкладок */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          <AnimatedCard className="p-6">
          {/* API Ключи */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              {/* Баннер бесплатного сервиса */}
              <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/20 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold text-white mb-2">🎉 Бесплатный сервис!</h2>
                  <p className="text-gray-300 mb-4">
                    Генерация описаний, названий и постов для ваших видео <strong className="text-purple-300">полностью бесплатна</strong>!
                  </p>
                  <p className="text-sm text-gray-400">
                    Мы используем единые API ключи для всех пользователей, чтобы сделать сервис доступным каждому.
                  </p>
                </div>
              </div>
              
              {/* Доступные функции */}
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <h3 className="font-bold text-lg text-white mb-4">💡 Что доступно бесплатно:</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { icon: '✨', text: 'Транскрибация через Whisper AI' },
                    { icon: '📝', text: 'Генерация названий и описаний' },
                    { icon: '📱', text: 'Создание постов для Telegram' },
                    { icon: '⏰', text: 'Автоматические таймкоды' },
                    { icon: '🎯', text: 'Оптимизация контента' },
                    { icon: '🚀', text: 'Публикация на YouTube' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <span className="text-xl">{item.icon}</span>
                      <span className="text-gray-300">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Телеграм */}
          {activeTab === 'telegram' && <Telegram />}

          {/* YouTube */}
          {activeTab === 'youtube' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">📹 Настройки YouTube API</h2>
                  <p className="text-gray-400 text-sm">Для публикации видео на YouTube</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={async () => {
                    await fetchYouTubeSettings(true); // Сохраняем введенные пользователем данные
                    showToast('✅ Настройки обновлены!', 'success');
                  }}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all"
                >
                  🔄 Обновить
                </motion.button>
              </div>

              {/* Инструкция по настройке */}
              <details className="group">
                <summary className="p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl cursor-pointer hover:from-blue-500/30 hover:to-purple-500/30 transition-all">
                  <span className="text-blue-300 font-medium">📖 Пошаговая инструкция по настройке YouTube API</span>
                  <span className="text-gray-400 text-sm ml-2">(нажмите, чтобы развернуть)</span>
                </summary>
                
                <div className="mt-4 space-y-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                  {/* Шаг 1 */}
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h4 className="text-white font-bold text-lg mb-2">🔷 Шаг 1: Создайте проект в Google Cloud</h4>
                    <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
                      <li>Перейдите на <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300">console.cloud.google.com</a></li>
                      <li>Войдите в аккаунт Google (тот же, что и YouTube канал)</li>
                      <li>Нажмите на выпадающее меню проектов (вверху слева)</li>
                      <li>Нажмите <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">NEW PROJECT</span></li>
                      <li>Введите название проекта (например: "AI Market Hub")</li>
                      <li>Нажмите <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">CREATE</span></li>
                    </ol>
                    <div className="mt-3 p-3 bg-slate-900 rounded-lg border border-slate-600">
                      <p className="text-gray-400 text-xs font-mono">
                        📸 Google Cloud Console → Select Project → New Project
                      </p>
                      <div className="mt-2 text-center text-6xl">🏗️</div>
                    </div>
                  </div>

                  {/* Шаг 2 */}
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="text-white font-bold text-lg mb-2">🔷 Шаг 2: Включите YouTube Data API v3</h4>
                    <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
                      <li>В боковом меню выберите <span className="text-yellow-400">APIs & Services</span> → <span className="text-yellow-400">Library</span></li>
                      <li>В поиске введите: <span className="bg-slate-700 px-2 py-0.5 rounded">YouTube Data API v3</span></li>
                      <li>Нажмите на результат поиска</li>
                      <li>Нажмите синюю кнопку <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">ENABLE</span></li>
                    </ol>
                    <div className="mt-3 p-3 bg-green-900/30 rounded-lg border border-green-600">
                      <p className="text-green-400 text-sm">✅ После включения API вы увидите страницу с метриками использования</p>
                    </div>
                  </div>

                  {/* Шаг 3 */}
                  <div className="border-l-4 border-yellow-500 pl-4">
                    <h4 className="text-white font-bold text-lg mb-2">🔷 Шаг 3: Настройте OAuth Consent Screen</h4>
                    <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
                      <li>Перейдите в <span className="text-yellow-400">APIs & Services</span> → <span className="text-yellow-400">OAuth consent screen</span></li>
                      <li>Выберите <span className="bg-purple-600 text-white px-2 py-0.5 rounded text-xs">External</span> и нажмите CREATE</li>
                      <li>Заполните обязательные поля:
                        <ul className="list-disc list-inside ml-4 mt-1 text-gray-400">
                          <li><strong>App name:</strong> AI Market Hub</li>
                          <li><strong>User support email:</strong> ваш email</li>
                          <li><strong>Developer contact:</strong> ваш email</li>
                        </ul>
                      </li>
                      <li>Нажмите <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">SAVE AND CONTINUE</span></li>
                      <li>На странице Scopes нажмите <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">ADD OR REMOVE SCOPES</span></li>
                      <li>Найдите и добавьте scope: <span className="bg-slate-700 px-2 py-0.5 rounded font-mono text-xs">https://www.googleapis.com/auth/youtube.upload</span></li>
                      <li>Нажмите UPDATE, затем SAVE AND CONTINUE</li>
                      <li>На странице Test users добавьте свой Google аккаунт</li>
                    </ol>
                  </div>

                  {/* Шаг 4 */}
                  <div className="border-l-4 border-purple-500 pl-4">
                    <h4 className="text-white font-bold text-lg mb-2">🔷 Шаг 4: Создайте OAuth 2.0 Client ID</h4>
                    <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
                      <li>Перейдите в <span className="text-yellow-400">APIs & Services</span> → <span className="text-yellow-400">Credentials</span></li>
                      <li>Нажмите <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">+ CREATE CREDENTIALS</span></li>
                      <li>Выберите <span className="text-cyan-400">OAuth client ID</span></li>
                      <li>Application type: <span className="bg-purple-600 text-white px-2 py-0.5 rounded text-xs">Web application</span></li>
                      <li>Name: <span className="bg-slate-700 px-2 py-0.5 rounded">AI Market Hub Web Client</span></li>
                      <li className="text-yellow-400 font-semibold">⚠️ ВАЖНО! Добавьте Authorized redirect URIs:</li>
                    </ol>
                    
                    <div className="mt-3 p-3 bg-yellow-900/30 rounded-lg border border-yellow-600">
                      <p className="text-yellow-400 text-sm font-semibold mb-2">Добавьте эти URI (нажмите + ADD URI):</p>
                      <div className="space-y-2">
                        <code className="block bg-slate-900 text-green-400 p-2 rounded text-xs break-all">
                          https://aimarkethub.ru/api/youtube/callback
                        </code>
                        <code className="block bg-slate-900 text-green-400 p-2 rounded text-xs break-all">
                          https://aimarkethub.ru/youtube-callback
                        </code>
                      </div>
                    </div>

                    <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm mt-3" start="7">
                      <li>Нажмите <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs">CREATE</span></li>
                      <li>Скопируйте <span className="text-green-400 font-bold">Client ID</span> и <span className="text-green-400 font-bold">Client Secret</span></li>
                    </ol>

                    <div className="mt-3 p-3 bg-green-900/30 rounded-lg border border-green-600">
                      <p className="text-green-400 text-sm">✅ Вставьте Client ID и Client Secret в поля ниже!</p>
                    </div>
                  </div>

                  {/* Шаг 5 */}
                  <div className="border-l-4 border-pink-500 pl-4">
                    <h4 className="text-white font-bold text-lg mb-2">🔷 Шаг 5: Получите Refresh Token</h4>
                    <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
                      <li>Заполните поля Client ID и Client Secret ниже</li>
                      <li>Нажмите кнопку <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-2 py-0.5 rounded text-xs">🔑 Получить автоматически</span></li>
                      <li>В открывшемся окне войдите в Google аккаунт</li>
                      <li>Разрешите доступ приложению</li>
                      <li>Refresh Token появится автоматически!</li>
                      <li>Нажмите <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-0.5 rounded text-xs">💾 Сохранить настройки</span></li>
                    </ol>
                  </div>

                  {/* Возможные ошибки */}
                  <div className="border-l-4 border-red-500 pl-4">
                    <h4 className="text-white font-bold text-lg mb-2">❌ Возможные ошибки</h4>
                    <div className="space-y-3 text-sm">
                      <div className="p-3 bg-red-900/30 rounded-lg border border-red-600">
                        <p className="text-red-400 font-semibold">Error 400: redirect_uri_mismatch</p>
                        <p className="text-gray-400 mt-1">Решение: Проверьте, что URI в настройках Google точно совпадает с указанными выше</p>
                      </div>
                      <div className="p-3 bg-red-900/30 rounded-lg border border-red-600">
                        <p className="text-red-400 font-semibold">Error 403: Access Denied</p>
                        <p className="text-gray-400 mt-1">Решение: Добавьте свой email в Test Users (OAuth consent screen)</p>
                      </div>
                      <div className="p-3 bg-red-900/30 rounded-lg border border-red-600">
                        <p className="text-red-400 font-semibold">API not enabled</p>
                        <p className="text-gray-400 mt-1">Решение: Включите YouTube Data API v3 (Шаг 2)</p>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Client ID</label>
                  <input
                    type="text"
                    value={youtubeSettings.client_id || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      updateYoutubeSettings(prev => ({ ...prev, client_id: newValue }));
                    }}
                    onBlur={(e) => {
                      // Сохраняем значение при потере фокуса
                      const newValue = e.target.value;
                      updateYoutubeSettings(prev => ({ ...prev, client_id: newValue }));
                    }}
                    className="input"
                    placeholder="xxxxx.apps.googleusercontent.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">OAuth 2.0 Client ID из Google Console</p>
                  <div className="mt-2 p-3 bg-yellow-500/20 border-2 border-yellow-500/50 rounded-lg">
                    <p className="text-sm text-yellow-300 font-semibold">⚠️ После ввода данных сразу нажмите "Сохранить настройки YouTube"</p>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">Client Secret</label>
                  <input
                    type="password"
                    value={youtubeSettings.client_secret || ''}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      updateYoutubeSettings(prev => ({ ...prev, client_secret: newValue }));
                    }}
                    onBlur={(e) => {
                      // Сохраняем значение при потере фокуса
                      const newValue = e.target.value;
                      updateYoutubeSettings(prev => ({ ...prev, client_secret: newValue }));
                    }}
                    className="input"
                    placeholder="GOCSPX-..."
                  />
                  <div className="mt-2 p-3 bg-yellow-500/20 border-2 border-yellow-500/50 rounded-lg">
                    <p className="text-sm text-yellow-300 font-semibold">⚠️ После ввода данных сразу нажмите "Сохранить настройки YouTube"</p>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">
                    Refresh Token
                    {authSuccess && (
                      <span className="ml-2 text-green-400 animate-pulse">✅ Получен автоматически!</span>
                    )}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="password"
                      value={youtubeSettings.refresh_token || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        updateYoutubeSettings(prev => ({ ...prev, refresh_token: newValue }));
                      }}
                      onBlur={(e) => {
                        const newValue = e.target.value;
                        updateYoutubeSettings(prev => ({ ...prev, refresh_token: newValue }));
                      }}
                      className={`input flex-1 transition-all duration-300 ${authSuccess ? 'ring-2 ring-green-500 bg-green-500/10' : ''}`}
                      placeholder="1//..."
                    />
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={async () => {
                        // В мобильном приложении рекомендуем использовать сайт
                        if (isNative()) {
                          const goToSite = confirm(
                            '📱 Для получения Refresh Token рекомендуется использовать сайт aimarkethub.ru\n\n' +
                            'В мобильном приложении OAuth авторизация Google может работать нестабильно.\n\n' +
                            '✅ Войдите на сайт aimarkethub.ru под своим аккаунтом\n' +
                            '✅ Откройте Настройки → YouTube API\n' +
                            '✅ Нажмите "Получить автоматически"\n' +
                            '✅ Токен синхронизируется автоматически\n\n' +
                            'Открыть сайт в браузере?'
                          );
                          if (goToSite) {
                            window.open('https://aimarkethub.ru/settings?tab=youtube', '_system');
                          }
                          return;
                        }
                        
                        try {
                          const res = await axios.get(`${API_URL}/youtube/auth-url`);
                          if (res.data.authUrl) {
                            window.open(res.data.authUrl, '_blank', 'width=600,height=700');
                            showToast('Откроется окно авторизации Google. После авторизации токен будет сохранен автоматически.', 'info');
                          }
                        } catch (error) {
                          showToast('Ошибка: ' + (error.response?.data?.error || error.message), 'error');
                        }
                      }}
                      className="px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium whitespace-nowrap"
                    >
                      🔐 Получить автоматически
                    </motion.button>
                  </div>
                  <div className="mt-2 p-3 bg-blue-500/20 border-2 border-blue-500/50 rounded-lg">
                    <p className="text-sm text-blue-300 font-semibold">💡 Если после авторизации Refresh Token не появился автоматически, нажмите кнопку "Обновить" в правом верхнем углу</p>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">Redirect URI</label>
                  <input
                    type="text"
                    value={youtubeSettings.redirect_uri || `https://${settings.domain || window.location.hostname}/api/youtube/auth/callback`}
                    onChange={(e) => {
                      const newValue = e.target.value.replace(/\s+/g, '');
                      updateYoutubeSettings(prev => ({ ...prev, redirect_uri: newValue }));
                    }}
                    onBlur={(e) => {
                      const newValue = e.target.value.replace(/\s+/g, '');
                      updateYoutubeSettings(prev => ({ ...prev, redirect_uri: newValue }));
                    }}
                    className="input font-mono text-sm"
                    placeholder={`https://${settings.domain || window.location.hostname}/api/youtube/auth/callback`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    <strong>ВАЖНО:</strong> Этот URI должен быть добавлен в Google Console в "Авторизованные URI перенаправления"
                  </p>
                </div>

                <div>
                  <label className="block text-gray-300 text-sm mb-2">Channel ID (опционально)</label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={youtubeSettings.channel_id || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        updateYoutubeSettings(prev => ({ ...prev, channel_id: newValue }));
                      }}
                      onBlur={(e) => {
                        const newValue = e.target.value;
                        updateYoutubeSettings(prev => ({ ...prev, channel_id: newValue }));
                      }}
                      className="input flex-1"
                      placeholder="UCxxxxx..."
                    />
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API_URL}/youtube/channel-id`);
                          if (res.data.success) {
                            updateYoutubeSettings(prev => ({ ...prev, channel_id: res.data.channelId }));
                            showToast(`✅ Channel ID получен! Канал: ${res.data.channelTitle}, ID: ${res.data.channelId}`, 'success', 5000);
                          }
                        } catch (error) {
                          showToast('❌ Ошибка: ' + (error.response?.data?.message || error.message), 'error');
                        }
                      }}
                      className="px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium whitespace-nowrap transition-all"
                    >
                      🔍 Получить автоматически
                    </motion.button>
                  </div>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSaveYouTube}
                className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold shadow-lg shadow-purple-500/30 transition-all"
              >
                💾 Сохранить настройки YouTube
              </motion.button>
            </div>
          )}
          </AnimatedCard>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
